/* What the events list is being asked for, as it lives in the URL — STORY-040.
 *
 * ── the URL is the state, and that is the whole design ──────────────────────
 * The story asks that a view can be linked and returned to. Keeping the filters
 * anywhere else — a store, component state, session storage — means a link that
 * does not carry them, and a back button that loses them. So this module owns
 * one translation, in both directions, and nothing else holds a copy.
 *
 * ── comma-separated, not repeated or JSON ───────────────────────────────────
 * `?status=idea,planned` rather than `?status=["idea","planned"]`. A URL a
 * person may paste into a message should be readable by the person pasting it,
 * and the API's repeated-parameter form is built from this at the edge.
 *
 * ── `within` is relative here and absolute on the wire ──────────────────────
 * The URL carries "the last 30 days" and the request carries a date. The story
 * evaluates a relative range against the **viewer's** today, and only the
 * viewer knows what day that is — a server-side "30 days ago" would answer
 * differently for two people opening the same link from two continents, and
 * neither would be told why.
 */

import {
  EVENT_SORTS,
  EVENT_STATUSES,
  EVENTS_PAGE_SIZE,
  type EventSort,
  type EventStatus,
  type ListEventsQuery,
  SORT_DIRECTIONS,
  type SortDirection,
} from '@eventuley/contracts';
import * as z from 'zod';

/** The relative ranges the story names. Not two date pickers. */
export const DATE_RANGES = [7, 30, 90, 365] as const;
export type DateRange = (typeof DATE_RANGES)[number];

/**
 * One comma-separated value, or several values, or one value.
 *
 * All three shapes have to be accepted, and finding that out cost a debugging
 * session: the router hands `?status=cancelled` back as `['cancelled']`, not as
 * the string that was written. A schema that took only the string quietly
 * caught its own failure and answered "no statuses" — a filter that hid
 * everything, from a URL that asked for one thing.
 *
 * This is a boundary and the query string is external input, so it parses
 * generously and normalises to one shape. `coding.md`: parse at the edge, trust
 * the type inside it.
 */
const commaSeparated = <T extends string>(values: readonly T[]) =>
  z.union([z.string(), z.array(z.string())]).transform((raw) => {
    const parts = (Array.isArray(raw) ? raw : raw.split(',')).flatMap((part) =>
      part.split(',').filter((one) => one !== ''),
    );

    const known = parts.filter((part): part is T => (values as readonly string[]).includes(part));

    /* Nothing asked for is an EMPTY selection — somebody unticked every box,
     * and the list should show nothing.
     *
     * Something asked for that means nothing is a BROKEN link, and it falls
     * back to the default view. Answering it with an empty selection was the
     * bug: `?status=postponed` showed zero events with no explanation, which
     * is the same screen as "your workspace is empty". The two inputs deserve
     * opposite answers and this is the only place that can tell them apart. */
    return parts.length > 0 && known.length === 0 ? undefined : known;
  });

export const eventsSearchSchema = z.object({
  /* Absent means the default view. An EMPTY list is a different thing —
   * somebody unticked every box — and must not fall back to the default, or a
   * filter would widen the list as you narrow it. */
  status: commaSeparated(EVENT_STATUSES).optional().catch(undefined),
  venue: z.string().uuid().optional().catch(undefined),
  organiser: z.string().uuid().optional().catch(undefined),
  within: z
    .union([z.literal(7), z.literal(30), z.literal(90), z.literal(365)])
    .optional()
    .catch(undefined),
  sort: z.enum(EVENT_SORTS).optional().catch(undefined),
  dir: z.enum(SORT_DIRECTIONS).optional().catch(undefined),
  /* One-based, because it appears in a URL a person reads. */
  page: z.number().int().min(1).optional().catch(undefined),
});

export type EventsSearch = z.infer<typeof eventsSearchSchema>;

/**
 * How many filters are on.
 *
 * Sorting is NOT a filter and is deliberately not counted: it changes the order
 * of the same set, so telling somebody "4 filters active" because they sorted by
 * name would send them hunting for a filter that is not hiding anything.
 */
export function activeFilterCount(search: EventsSearch): number {
  return [search.status, search.venue, search.organiser, search.within].filter(
    (value) => value !== undefined,
  ).length;
}

/** Today where the reader is standing, as `YYYY-MM-DD`. */
export function readerToday(now: Date = new Date()): string {
  /* `en-CA` formats as `YYYY-MM-DD`, which is what a date column already is, so
   * the comparison stays a string comparison and needs no parsing. The locale
   * is a formatting device here and never the reader's. */
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  }).format(now);
}

/**
 * The earliest date a relative range includes, from the reader's own today.
 *
 * Anchored on `readerToday` and then counted back in whole days, rather than
 * subtracting milliseconds from a timestamp. Doing it in the calendar means the
 * answer does not shift by a day when the range spans a daylight-saving change
 * — which "last 90 days" always will, twice a year, in most of the world.
 */
export function earliestDate(within: DateRange, now: Date = new Date()): string {
  const [year, month, day] = readerToday(now).split('-').map(Number);
  /* Built in UTC and read back in UTC, so no zone is involved at all: this is
   * calendar arithmetic on a date the reader's zone has already decided. */
  const anchor = new Date(Date.UTC(year ?? 1970, (month ?? 1) - 1, day ?? 1));
  anchor.setUTCDate(anchor.getUTCDate() - within);
  return anchor.toISOString().slice(0, 10);
}

/**
 * The shape the URL actually holds, as opposed to the shape a screen reads.
 *
 * `status` is a comma-separated string here and an array once parsed, and the
 * two are genuinely different types — the schema transforms between them. A
 * navigation writes the URL, so it writes this one.
 */
export interface EventsSearchState {
  status?: string | undefined;
  venue?: string | undefined;
  organiser?: string | undefined;
  within?: DateRange | undefined;
  sort?: EventSort | undefined;
  dir?: SortDirection | undefined;
  page?: number | undefined;
}

/** A screen's filters, as the URL holds them. */
export function toSearchState(search: EventsSearch): EventsSearchState {
  return {
    /* An EMPTY selection has to survive the round trip, and an empty string
     * would parse back to an empty array — which is what it means. Dropping the
     * key instead would silently restore the default view, so unticking every
     * box would WIDEN the list. */
    ...(search.status === undefined ? {} : { status: search.status.join(',') }),
    ...(search.venue === undefined ? {} : { venue: search.venue }),
    ...(search.organiser === undefined ? {} : { organiser: search.organiser }),
    ...(search.within === undefined ? {} : { within: search.within }),
    ...(search.sort === undefined ? {} : { sort: search.sort }),
    ...(search.dir === undefined ? {} : { dir: search.dir }),
    ...(search.page === undefined ? {} : { page: search.page }),
  };
}

/**
 * Re-exported, never re-declared.
 *
 * The API adapter and its fake both read `EVENTS_PAGE_SIZE` from the contract.
 * A second 25 here would go on agreeing with them until somebody changed one,
 * and then the client's offsets and its "showing 26–50 of 40" would be quietly
 * wrong while every test still passed.
 */
export const PAGE_SIZE = EVENTS_PAGE_SIZE;

/** The URL's filters, as the API wants them. */
export function toQuery(search: EventsSearch, now: Date = new Date()): ListEventsQuery {
  const page = search.page ?? 1;

  return {
    ...(search.status ? { status: search.status as EventStatus[] } : {}),
    ...(search.venue ? { venueId: search.venue } : {}),
    ...(search.organiser ? { organiserId: search.organiser } : {}),
    ...(search.within ? { from: earliestDate(search.within, now) } : {}),
    ...(search.sort ? { sort: search.sort as EventSort } : {}),
    ...(search.dir ? { direction: search.dir as SortDirection } : {}),
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  };
}

/** The API's query, as a query string. */
export function toSearchParams(query: ListEventsQuery): string {
  const params = new URLSearchParams();

  /* Repeated rather than comma-joined, because that is the form the API's
   * schema accepts.
   *
   * An empty selection never reaches here: the screen answers "nothing matches"
   * itself rather than asking the server which of no statuses to show. The API
   * handles the empty set correctly and is tested for it — this is a request
   * not worth making, not a case not worth handling. */
  for (const status of query.status ?? []) params.append('status', status);

  if (query.venueId) params.set('venueId', query.venueId);
  if (query.organiserId) params.set('organiserId', query.organiserId);
  if (query.from) params.set('from', query.from);
  if (query.sort) params.set('sort', query.sort);
  if (query.direction) params.set('direction', query.direction);
  if (query.limit !== undefined) params.set('limit', String(query.limit));
  if (query.offset !== undefined) params.set('offset', String(query.offset));

  const rendered = params.toString();
  return rendered === '' ? '' : `?${rendered}`;
}
